import React, { useState } from 'react';
import { Banknote, CreditCard, FileText, UserCheck, Check } from 'lucide-react';
import { PaymentMethod, PAYMENT_METHOD_CONFIGS } from '../../types/pos';
import { Input } from '../ui/Input';

interface PaymentMethodSelectorProps {
  selectedMethod: PaymentMethod;
  onSelect: (method: PaymentMethod) => void;
  workerName?: string;
  onWorkerNameChange?: (name: string) => void;
  disabled?: boolean;
}

const iconMap = {
  Banknote,
  CreditCard,
  FileText,
  UserCheck
};

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onSelect,
  workerName = '',
  onWorkerNameChange,
  disabled = false
}) => {
  const [showWorkerInput, setShowWorkerInput] = useState(selectedMethod === 'worker_pickup');

  const handleSelect = (method: PaymentMethod) => {
    if (disabled) return;
    onSelect(method);
    setShowWorkerInput(method === 'worker_pickup');
  };

  const methods: PaymentMethod[] = ['cash', 'pin', 'loan', 'worker_pickup'];

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Payment Method
      </label>

      <div className="grid grid-cols-2 gap-3">
        {methods.map(method => {
          const config = PAYMENT_METHOD_CONFIGS[method];
          const Icon = iconMap[config.icon as keyof typeof iconMap];
          const isSelected = selectedMethod === method;

          return (
            <button
              key={method}
              onClick={() => handleSelect(method)}
              disabled={disabled}
              className={`relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 ${
                isSelected
                  ? `${config.borderColor} ${config.bgColor} ${config.color} shadow-md`
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <Check className="h-4 w-4" />
                </div>
              )}

              <Icon className={`h-8 w-8 mb-2 ${isSelected ? '' : 'text-gray-400 dark:text-gray-500'}`} />

              <span className="font-semibold text-sm">{config.label}</span>

              <span className={`text-xs mt-1 text-center leading-tight ${
                isSelected ? 'opacity-90' : 'text-gray-400 dark:text-gray-500'
              }`}>
                {config.description}
              </span>
            </button>
          );
        })}
      </div>

      {showWorkerInput && (
        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Worker Name / Employee ID *
          </label>
          <Input
            placeholder="Enter worker name or ID"
            value={workerName}
            onChange={(e) => onWorkerNameChange?.(e.target.value)}
            disabled={disabled}
            className="bg-white dark:bg-gray-900"
          />
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            Required for internal tracking and accountability
          </p>
        </div>
      )}

      <div className={`p-3 rounded-lg border ${
        selectedMethod === 'cash' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
        selectedMethod === 'pin' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' :
        selectedMethod === 'loan' ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' :
        'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            selectedMethod === 'cash' ? 'bg-green-500' :
            selectedMethod === 'pin' ? 'bg-blue-500' :
            selectedMethod === 'loan' ? 'bg-orange-500' :
            'bg-gray-500'
          }`} />
          <span className={`text-sm font-medium ${PAYMENT_METHOD_CONFIGS[selectedMethod].color}`}>
            {selectedMethod === 'cash' && 'Payment will be marked as PAID (immediate)'}
            {selectedMethod === 'pin' && 'Payment will be marked as PAID (card terminal)'}
            {selectedMethod === 'loan' && 'Invoice created as UNPAID (on credit)'}
            {selectedMethod === 'worker_pickup' && 'Internal pickup (no payment required)'}
          </span>
        </div>
      </div>
    </div>
  );
};
